import { useEffect, useMemo, useState } from 'react';

import type {
  ClinicRelationship,
  TradeStatusCounts,
} from '@/entities/business-relationship/api/businessRelationship.api';
import {
  getRelationshipGroup,
  RELATIONSHIP_GROUP_LABEL,
  type RelationshipGroup,
} from '@/entities/business-relationship/lib/statusView';
import { SearchInput } from '@/shared/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { cn } from '@/shared/lib/utils';

import { AccountListItem } from './AccountListItem';

const GROUP_ORDER: RelationshipGroup[] = ['ongoing', 'applying', 'closed'];

/**
 * M-04 좌측 목록 카드 (Figma container/left › list 485:4659) — 병의원을 고르는 선택 전용 목록.
 * 거래 중 · 거래 신청 · 종료·거절을 탭(Tab Small Square)으로 나눈다. 탭 숫자는 서버 counts, 검색 중이면 걸러진 건수.
 * 순서는 서버 정렬 그대로. 검색은 받은 목록에서 병의원명으로 거른다 (서버 keyword와 같은 조건)
 */
export const AccountList = ({
  items,
  counts,
  selectedId,
}: {
  items: ClinicRelationship[];
  counts?: TradeStatusCounts;
  selectedId?: number;
}) => {
  const [keyword, setKeyword] = useState('');
  const selectedGroup = useMemo(() => {
    const selected = items.find((i) => i.relationshipId === selectedId);
    return selected ? getRelationshipGroup(selected.statusView) : undefined;
  }, [items, selectedId]);
  const [tab, setTab] = useState<RelationshipGroup>(selectedGroup ?? 'ongoing');

  // 다른 그룹의 거래처가 선택되면(URL 이동 · 첫 자동 선택) 그 탭을 연다
  useEffect(() => {
    if (selectedGroup) setTab(selectedGroup);
  }, [selectedGroup]);

  const grouped = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    const filtered = q
      ? items.filter((i) => i.clinicName.toLowerCase().includes(q))
      : items;
    return Object.fromEntries(
      GROUP_ORDER.map((g) => [
        g,
        filtered.filter((i) => getRelationshipGroup(i.statusView) === g),
      ]),
    ) as Record<RelationshipGroup, ClinicRelationship[]>;
  }, [items, keyword]);

  const countOf = (g: RelationshipGroup) =>
    keyword.trim() || !counts ? grouped[g].length : counts[g];
  const shown = grouped[tab];

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-body-15-sb text-fg-primary">거래처</h2>
      <SearchInput
        placeholder="병의원명 검색"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        aria-label="병의원명 검색"
      />
      <div className="flex flex-col gap-2">
        <Tabs value={tab} onValueChange={(v) => setTab(v as RelationshipGroup)}>
          {/* Figma Tab 485:4661 — 바탕 r16 · 칩 높이 40 · r12 (라이브러리 smallSquare보다 크다) */}
          <TabsList variant="smallSquare" className="rounded-16">
            {GROUP_ORDER.map((g) => (
              <TabsTrigger
                key={g}
                value={g}
                className="group h-10 rounded-12 px-1"
              >
                {RELATIONSHIP_GROUP_LABEL[g]}
                <span className="ml-1.5 group-data-[state=active]:text-fg-accent">
                  {countOf(g)}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {shown.length === 0 ? (
          <p
            className={cn('py-10 text-center text-body-14-m text-fg-tertiary')}
          >
            {keyword.trim()
              ? '검색 결과가 없어요.'
              : `${RELATIONSHIP_GROUP_LABEL[tab]} 거래처가 없어요.`}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {shown.map((item) => (
              <li key={item.relationshipId}>
                <AccountListItem
                  item={item}
                  group={tab}
                  selected={item.relationshipId === selectedId}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
